import { DeviceTypeSettings } from '@services/types';

export type DeviceTypeSettingFormItem =
  | DeviceTypeSettingFormItemString
  | DeviceTypeSettingFormItemNumber
  | DeviceTypeSettingFormItemBool;

type DeviceTypeSettingFormItemString = {
  field: keyof DeviceTypeSettings;
  label: string;
  description: {
    header: string;
    rules: string[];
  };
  type: 'string';
  value?: {
    min?: number;
    max?: number;
    default: string;
  };
};

type DeviceTypeSettingFormItemNumber = {
  field: keyof DeviceTypeSettings;
  label: string;
  description: {
    header: string;
    rules: string[];
  };
  type: 'number';
  value: {
    min?: number;
    max?: number;
    default: number;
  };
};

type DeviceTypeSettingFormItemBool = {
  field: keyof DeviceTypeSettings;
  label: string;
  description: {
    header: string;
    rules: string[];
  };
  type: 'boolean';
  value: {
    default: boolean;
  };
};

interface FormConfig {
  string: DeviceTypeSettingFormItemString[];
  number: DeviceTypeSettingFormItemNumber[];
  bool: DeviceTypeSettingFormItemBool[];
}

export const formConfig: FormConfig = {
  string: [
    {
      field: 'fwExtraName1',
      label: 'Custom fw 1',
      type: 'string',
      description: {
        header: 'Custom Firmware Name 1. Leave empty to disable.',
        rules: [
          'Value Type: String',
          'If value is set f.e. to PPP, then firmware of type PPP can be uploaded as well as LOADER, UI/EDA, FW.<br /> Also link to this firmware will be sent to device on each correct request.',
          'Default empty',
        ],
      },
    },
    {
      field: 'fwExtraName2',
      label: 'Custom fw 2',
      type: 'string',
      description: {
        header: 'Custom Firmware Name 2. Leave empty to disable.',
        rules: [
          'Value Type: String',
          'If value is set f.e. to PPP, then firmware of type PPP can be uploaded as well as LOADER, UI/EDA, FW.<br /> Also link to this firmware will be sent to device on each correct request.',
          'Default empty',
        ],
      },
    },
    {
      field: 'fwExtraName3',
      label: 'Custom fw 3',
      type: 'string',
      description: {
        header: 'Custom Firmware Name 3. Leave empty to disable.',
        rules: [
          'Value Type: String',
          'If value is set f.e. to PPP, then firmware of type PPP can be uploaded as well as LOADER, UI/EDA, FW.<br /> Also link to this firmware will be sent to device on each correct request.',
          'Default empty',
        ],
      },
    },
    {
      field: 'urlPreProd',
      label: 'Url preProd',
      type: 'string',
      description: {
        header: 'This option enable a per device flag that allows to move a device to another server (preprod)',
        rules: [
          'Value Type: String',
          'In the case of a production server, the server may send this url to indicate that the stb should go into a preproduction server. In the admin server, this will be done by clicking on the button "move to preprod"',
          'Note: url should start with http:// or https://',
          'Default empty',
        ],
      },
    },
  ],
  number: [
    {
      field: 'loaderMaxRetries',
      label: 'Loader retries',
      type: 'number',
      value: {
        default: 15,
        min: 1,
        max: 1000,
      },
      description: {
        header: 'Max retries counter for LOADER firmware',
        rules: ['Value Type: Integer', 'Value Range: [1..1000]', 'Default: 15'],
      },
    },
    {
      field: 'fwMaxRetries',
      label: 'FW retries',
      type: 'number',
      value: {
        default: 15,
        min: 1,
        max: 1000,
      },
      description: {
        header: 'Max retries counter for FW firmware',
        rules: ['Value Type: Integer', 'Value Range: [1..1000]', 'Default: 15'],
      },
    },
    {
      field: 'uiMaxRetries',
      label: 'UI retries',
      type: 'number',
      value: {
        default: 15,
        min: 1,
        max: 1000,
      },
      description: {
        header: 'Max retries counter for UI firmware',
        rules: ['Value Type: Integer', 'Value Range: [1..1000]', 'Default: 15'],
      },
    },
  ],
  bool: [
    {
      field: 'eda',
      label: 'EDA',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header: 'Max retries counter for UI firmware',
        rules: ['Value Type: Integer', 'Value Range: [1..1000]', 'Default: 15'],
      },
    },
    {
      field: 'allowDowngradeUr',
      label: 'Enable Forced rules',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header: 'Enable forced rule checkbox in rule screen',
        rules: [
          'The forced rule allows the server to send a firmware download url even if the stb version of the package is higher. They can be used to downgrade device if device support it.',
          'Value Type: Boolean',
          'Default: false',
        ],
      },
    },
    {
      field: 'disableCheckSn',
      label: 'Disable Serial number check',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header:
          'Enable to allow devices to reconnect with a different serial number (The new S/N replace the previous one in database)',
        rules: ['Value Type: Boolean', 'Default: false'],
      },
    },
    {
      field: 'disableTagUrl',
      label: 'Enable to remove the url tag',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header:
          'By default a tag with device mac address is added to download urls to be able to track device download in CDN logs.',
        rules: ['Value Type: Boolean', 'Default: false'],
      },
    },
    {
      field: 'enableQuota',
      label: 'Enable quota',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header: 'Enable quota',
        rules: [
          'This feature enables setting quota of update urls for devices per Stb Group',
          'Value Type: Boolean',
          'Default: false',
        ],
      },
    },
    {
      field: 'addBPProfile',
      label: 'Add BP Profile',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header: 'BP Profile',
        rules: [
          'This feature enables setting deployment mode for Stb Group to one of values [user, recommended, mandatory]',
          'Value Type: Boolean',
          'Default: false',
          'Note: not all devices support it',
        ],
      },
    },
    {
      field: 'allowUidUpdate',
      label: 'Reset device on usage id change',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header: 'Delete and recreate device in default group when device connect with a new device type',
        rules: ['Value Type: Boolean', 'Default: false'],
      },
    },
    {
      field: 'urSendAllAtOnce',
      label: 'Send all firmwares at once',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header:
          'Enable check box "Send all at once" in UI upgrade rule screen. Should only be enabled if device support it.',
        rules: ['Value Type: Boolean', 'Default: false'],
      },
    },
    {
      field: 'enableDeploymentMode',
      label: 'Enable deployment mode',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header:
          'This feature enables setting deployment mode for Stb Group to one of values [user, recommended, mandatory]',
        rules: ['Value Type: Boolean', 'Default: false', 'Note: not all devices support it'],
      },
    },
    {
      field: 'enableStbAuth',
      label: 'Enable STB Auth',
      type: 'boolean',
      value: {
        default: false,
      },
      description: {
        header: 'Enable STB Auth',
        rules: [
          'Enable signature authentication feature.\n' +
            'When enabled, devices requests must provide valid authentication header to be authorized.',
          'Value Type: Boolean',
          'Default: false',
        ],
      },
    },
  ],
};
